import { useEffect, useState } from 'react';
import { getPartnerDashboardService, getPartnerWorkspaceService } from '../../../services/dynamicDashboard.service';
import { findSection, findCard } from '../../DashboardCommon/dashboardCards';
import { analyzeRowData } from '../../PartnerDashboard/pages/AttentionNeeded.page';
import { SUPPRESSION_MIN_N, isSuppressedCell } from '../../../utils/suppression';

/**
 * Real data for the Growth Opportunities pages (LA-PW-20Oct-02).
 *
 * The figures come from the partner's own Partner Dashboard — the same data
 * house sections, through the same API, that the Partner Dashboard pages read:
 *   S_8  "By School"        — one row per school: participants and the
 *                             Dormant / Data weak / Low coverage / High risk flags
 *   S_20 "Program Deployment" — My Programs and Teacher-Created KPIs, and the
 *                             per-program table with its completion rate
 * and from the partner workspace (PW) document (usePartnerWorkspace):
 *   adoption[]                      — per-solution reach, active learners/orgs, rate, status
 *   portfolio.block3 / block4       — priority areas, active teachers / learning outcomes
 *   program_adoption.usage_by_quarter — modules started / completed per quarter
 * Nothing is computed that the data house does not already send.
 */
export const usePartnerDashboardSections = (sectionCodes) => {
    const key = sectionCodes.join(',');
    const [state, setState] = useState({ loading: true, data: null });

    useEffect(() => {
        let cancelled = false;
        setState({ loading: true, data: null });
        getPartnerDashboardService({ card_sections: key })
            .then((res) => { if (!cancelled) setState({ loading: false, data: res?.data || null }); })
            .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
        return () => { cancelled = true; };
    }, [key]);

    return state;
};

/** Statuses that ask for partner action — the Attention Needed rows. */
export const ATTENTION_STATUSES = ['Dormant', 'Data weak', 'Low coverage', 'High risk'];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * S_8 as one row per school: its status, the sentence and action the Partner
 * Dashboard's Attention Needed page uses (analyzeRowData), and the counts.
 */
export const schoolRowsFrom = (dashboardData) => {
    const table = (findSection(dashboardData, 'S_8')?.cards || []).find((c) => c.card_type === 'TableCard');
    const cols = table?.table_objects || [];
    const col = (names) => cols.find((c) => names.includes(c.column_name));

    const schoolCol = col(['School']);
    const countryCol = col(['Country']);
    const anyRiskCol = col(['Any-risk']);
    const participationCol = col(['Participation %']);
    const activeSchoolsCol = col(['Active Schools']);
    const registeredSchoolsCol = col(['Registered Schools']);
    const activeParticipantsCol = col(['Active Participants']);
    const registeredParticipantsCol = col(['Registered Participants']);
    const dormantCol = col(['Dormant Status']);
    const dataWeakCol = col(['Data Weak Status']);
    const lowCoverageCol = col(['Low Participation Status']);
    const atRiskCol = col(['At Risk Status']);
    const activeCol = col(['Active Status']);
    const growthCol = col(['Growth Opportunity Status']);

    return (schoolCol?.values || []).map((_, i) => ({
        ...analyzeRowData({
            client: schoolCol?.values?.[i],
            country: countryCol?.values?.[i],
            anyRisk: anyRiskCol?.values?.[i],
            coverage: participationCol?.values?.[i],
            assessed: activeParticipantsCol?.values?.[i],
            dormant: dormantCol?.values?.[i],
            dataWeak: dataWeakCol?.values?.[i],
            lowCoverage: lowCoverageCol?.values?.[i],
            highRisk: atRiskCol?.values?.[i],
            active: activeCol?.values?.[i],
            growth: growthCol?.values?.[i],
        }),
        activeSchools: num(activeSchoolsCol?.values?.[i]),
        registeredSchools: num(registeredSchoolsCol?.values?.[i]),
        activeParticipants: num(activeParticipantsCol?.values?.[i]),
        registeredParticipants: num(registeredParticipantsCol?.values?.[i]),
    }));
};

/** A S_20 KPI card's kpi_data, e.g. ('C_2') for My Programs, ('C_6') for Teacher-Created. */
export const programKpi = (dashboardData, cardCode) => findCard(dashboardData, 'S_20', cardCode)?.kpi_data || null;

/** S_20 "Programs & deployment status": { program, status, rate } per program. */
export const programRowsFrom = (dashboardData) => {
    const cols = findCard(dashboardData, 'S_20', 'C_10')?.table_objects || [];
    const col = (name) => cols.find((c) => c.column_name === name);
    const programCol = col('Program');
    const statusCol = col('Status');
    const rateCol = col('Completion Rate %');
    return (programCol?.values || []).map((program, i) => ({
        program,
        status: statusCol?.values?.[i],
        rate: rateCol?.values?.[i],
    }));
};

/** The PW workspace document's machine values (response data.data), or null on failure / no document. */
export const usePartnerWorkspace = () => {
    const [state, setState] = useState({ loading: true, data: null });

    useEffect(() => {
        let cancelled = false;
        getPartnerWorkspaceService()
            .then((res) => { if (!cancelled) setState({ loading: false, data: res?.data?.data || null }); })
            .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
        return () => { cancelled = true; };
    }, []);

    return state;
};

const NO_DATA = '—';
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const withUnit = (n, unit) => (unit ? `${n.toLocaleString()} ${unit}` : n.toLocaleString());
const pct = (v) => (isNum(v) ? `${v}%` : NO_DATA);

/** Solution Adoption rows in screen order; adoption[] is matched by key, not by position. */
export const SOLUTION_ROWS = [
    { key: 'cos_readiness_solution', solution: 'COS Readiness Solution' },
    { key: 'cos_children_assessment', solution: 'COS Children Assessment' },
    { key: 'learncreator', solution: 'LearnCreator' },
    { key: 'cos_government_checklist', solution: 'COS Government Checklist' },
];

export const adoptionRowsFrom = (ws) => SOLUTION_ROWS.map(({ key, solution }) => {
    const a = (ws?.adoption || []).find((x) => x?.key === key) || {};
    return {
        key,
        solution,
        reach: isNum(a.reach) ? withUnit(a.reach, a.reach_unit) : NO_DATA,
        activeLearner: isNum(a.active_learners) ? a.active_learners.toLocaleString() : NO_DATA,
        activeOrg: isNum(a.active_orgs) ? withUnit(a.active_orgs, a.reach_unit) : NO_DATA,
        adoptionRate: pct(a.adoption_rate),
        status: ['Active', 'Growth opportunity'].includes(a.status) ? a.status : null,
    };
});

/** The last 4 quarters up to `now`'s, oldest first, as 'Qn YYYY'; quarters with no row are 0. */
export const usageByQuarterFrom = (ws, now = new Date()) => {
    const rows = ws?.program_adoption?.usage_by_quarter;
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const byQuarter = {};
    rows.forEach((r) => { byQuarter[r?.quarter] = r; });
    const current = now.getFullYear() * 4 + Math.floor(now.getMonth() / 3);
    return [3, 2, 1, 0].map((back) => {
        const year = Math.floor((current - back) / 4);
        const q = ((current - back) % 4) + 1;
        const row = byQuarter[`${year}-Q${q}`];
        return { quarter: `Q${q} ${year}`, 'Modules started': num(row?.started), 'Modules completed': num(row?.completed) };
    });
};

/** Learning outcomes (block4) — scores, so n < SUPPRESSION_MIN_N or a suppressed flag shows "—" for both. */
export const outcomesFrom = (ws) => {
    const b4 = ws?.portfolio?.block4;
    const hidden = !b4 || isSuppressedCell(b4) || Number(b4.n_users) < SUPPRESSION_MIN_N;
    return [
        { label: 'Competency acquisition', value: hidden ? NO_DATA : pct(b4.acquisition_rate) },
        { label: 'Competency improvement', value: hidden ? NO_DATA : pct(b4.improvement_rate) },
    ];
};
